import { ETextStyleVariant, GeneralMapPinOutlineIcon, Text } from '@outdoorsyco/bonfire';
import { clsx } from 'clsx';
import React from 'react';
import { useFocusRing } from 'react-aria';

import { ILocationSearchAutocompleteListOption } from '@/hooks/useLocationSearchAutocompleteOptions';

import { ELocationSearchAutocompleteListVariant } from './LocationSearchAutocompleteList';

interface LocationSearchAutocompleteListItemProps {
  option: ILocationSearchAutocompleteListOption;
  optionIndex: number;
  optionProps?: React.HTMLAttributes<HTMLDivElement> & { key?: string | number };
  totalOptions: number;
  variant: ELocationSearchAutocompleteListVariant;
  defaultOptionPerColumnMax?: number;
  isInLastGroup: boolean;
}

export const LocationSearchAutocompleteListItem: React.FC<
  LocationSearchAutocompleteListItemProps
> = ({
  option,
  optionIndex,
  optionProps,
  totalOptions,
  variant,
  defaultOptionPerColumnMax,
  isInLastGroup,
}) => {
  const optionPerColumnMax = defaultOptionPerColumnMax ?? 0;

  // Bonfire's focus ring, wired up the way the search fields and tabs do it:
  // react-aria raises it only while focus is visible, so it shows on Tab and stays
  // off pointer clicks. Lists whose options are not keyboard focusable (the
  // horizontal variant leaves them at `tabIndex: -1`) simply never raise it.
  const { isFocusVisible, focusProps } = useFocusRing();

  const { key, ...props } = optionProps || {};

  const hideBottomBorder = React.useMemo(() => {
    switch (variant) {
      case ELocationSearchAutocompleteListVariant.Horizontal:
        if (totalOptions > optionPerColumnMax) {
          const isLastItemInFirstColumn = optionIndex === Math.min(2, totalOptions - 1);
          const isLastItem = optionIndex === totalOptions - 1;
          const columnsNotFullyFilled = totalOptions < optionPerColumnMax * 2;

          return isLastItemInFirstColumn || (isLastItem && !columnsNotFullyFilled);
        }
        return false;
      case ELocationSearchAutocompleteListVariant.Vertical:
        return isInLastGroup && optionIndex === totalOptions - 1;
      default:
        return false;
    }
  }, [variant, totalOptions, optionPerColumnMax, optionIndex, isInLastGroup]);

  return (
    <div
      className={clsx('flex flex-col py-3 truncate border-b border-gray-200 cursor-pointer gap-4', {
        'border-none': hideBottomBorder,
      })}
      key={key}>
      <div
        className={clsx(
          'flex items-center w-full p-1 gap-4 rounded-2xl hover:bg-gray-10',
          // `rounded-2xl` above keeps the shadow on the row's own shape.
          isFocusVisible && 'focusRing',
        )}
        {...props}
        {...focusProps}>
        {/* Hidden on the container, not on the icons: the `option.icon` factories are
            zero-argument arrows, so props handed to them (aria-hidden included) are
            dropped. The option is named by its label text either way. */}
        <div
          className="flex items-center justify-center bg-gray-10 w-14 h-14 rounded-2xl shrink-0"
          aria-hidden>
          {option.icon ? (
            <option.icon />
          ) : (
            <GeneralMapPinOutlineIcon className="w-6 h-6 text-gray-900" />
          )}
        </div>
        <div className="flex flex-col flex-1 min-w-0">
          <Text
            key={option.value?.mapbox_id || `${option.label}-${optionIndex}`}
            variant={ETextStyleVariant.MediumRegular}
            className="text-left text-black truncate">
            {option.label}
          </Text>
          {!!option.sublabel && (
            <Text
              variant={ETextStyleVariant.LegalRegular}
              className="text-left truncate text-gray-50">
              {option.sublabel}
            </Text>
          )}
        </div>
      </div>
    </div>
  );
};
